import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Content, Overlay, Root } from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { styled } from '@components-shared/src/stitches';
import { useVisualViewport } from '@hooks/useVisualViewport';
import { mergeRefs } from '../../../../../services/webapp/utils/merge-refs';
import { DataTestId } from '../../../../../services/webapp/utils/test';
import { useIsOverflow } from '../../hooks/useIsOverflow';
import { StyledScroll } from '../containers/RadixScroll';
import { IconButton } from '../icon-buttons/IconButton';

export interface ModalProps {
  backCallToAction?: React.ReactNode;
  body: React.ReactNode;
  closeModal?: () => void;
  callToAction?: React.ReactNode;
  isCentered?: boolean;
  isFullScreen?: boolean;
  size?: 'small' | 'medium' | 'large';
  subHeader?: React.ReactNode;
  title?: string;
  showHeaderShadow?: boolean;
  showFooterShadow?: boolean;
  bodyWithoutPadding?: boolean;
  bodyWithoutPaddingBottom?: boolean;
  overlayClassName?: string;
  containerClassName?: string;
  bodyClassName?: string;
  preventClose?: boolean;
}

export const StandardModal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      backCallToAction,
      body,
      closeModal,
      callToAction,
      isCentered = false,
      isFullScreen = false,
      showHeaderShadow = false,
      showFooterShadow = false,
      size = 'small',
      subHeader,
      title,
      bodyWithoutPadding = false,
      bodyWithoutPaddingBottom = false,
      overlayClassName,
      containerClassName,
      bodyClassName,
      preventClose,
    },
    ref,
  ) => {
    const bodyRef = useRef<HTMLDivElement>(null);
    const hasFooter = !!callToAction || !!backCallToAction;
    const isBodyOverflow = useIsOverflow(bodyRef);
    const { height: visualViewportHeight } = useVisualViewport();

    const renderTitleContainer = (
      <TitleContainer>
        {!!title && (
          <Title withSubtext={!!subHeader} isCentered={isCentered}>
            {title}
          </Title>
        )}
      </TitleContainer>
    );

    const content = (
      <ModalOverlay
        // Take safari bottom menu on mobile and visual keyboards into account
        // Visual viewport will be 0 on mount, so we're using the height of viewport as fallback
        // If we don't do this, the modal animate from top to bottom, caused by framer motion layout
        css={{ maxHeight: visualViewportHeight || '100vh' }}
        className={overlayClassName}
        isFullScreen={isFullScreen}
      >
        <Container
          className={containerClassName}
          onOpenAutoFocus={(event: Event) => event.preventDefault()}
          size={size}
          isFullScreen={isFullScreen}
        >
          <HeaderContainer showShadow={showHeaderShadow || isBodyOverflow}>
            <TitleAndCloseButtonContainer isCentered={isCentered}>
              {!isCentered && renderTitleContainer}
              {!!closeModal && (
                <IconButton
                  icon={
                    <FontAwesomeIcon
                      icon={faXmark}
                      size="lg"
                      data-testid={DataTestId.MODAL_CLOSE_BUTTON}
                    />
                  }
                  onClick={closeModal}
                />
              )}
            </TitleAndCloseButtonContainer>
            {subHeader}
          </HeaderContainer>
          <Body
            ref={mergeRefs([bodyRef, ref])}
            withSubtext={!!subHeader}
            isCentered={isCentered}
            withoutPadding={!subHeader && !title}
            withoutPaddingBottom={bodyWithoutPaddingBottom}
            className={clsx(bodyClassName, isBodyOverflow && bodyWithoutPaddingBottom && 'pb-4')}
          >
            {body}
          </Body>
          {isCentered && renderTitleContainer}
          {hasFooter && (
            <Footer
              showShadow={showFooterShadow || isBodyOverflow}
              withoutPadding={bodyWithoutPadding}
            >
              {!!backCallToAction && backCallToAction}
              {!!callToAction && callToAction}
            </Footer>
          )}
        </Container>
      </ModalOverlay>
    );

    return preventClose ? <Root open>{content}</Root> : <>{content}</>;
  },
);

StandardModal.displayName = 'StandardModal';

const Container = styled(Content, {
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: '$white',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px -8px 28px rgba(0, 0, 0, 0.28)',
  padding: '16px 0px',
  zIndex: '$modal',
  width: '100%',
  maxHeight: '100%',

  '> *': {
    paddingX: 16,
  },

  '@tablet': {
    width: 'unset',
    padding: '24px 0px',
    maxHeight: 'calc(100vh - 200px)',
    '> *': {
      paddingX: 24,
    },
  },
  variants: {
    size: {
      small: {
        '@tablet': {
          width: 420,
        },
      },
      medium: {
        '@tablet': {
          width: 600,
        },
      },
      large: {
        '@tablet': {
          width: 830,
        },
      },
    },
    isFullScreen: {
      true: {
        width: '100vw',
        height: '100vh',
        borderRadius: 0,
      },
    },
  },

  defaultVariants: {
    size: 'small',
  },

  compoundVariants: [
    {
      isFullScreen: true,
      size: 'small',
      css: {
        '@tablet': {
          width: 420,
          borderRadius: 16,
        },
      },
    },
    {
      isFullScreen: true,
      size: 'medium',
      css: {
        '@tablet': {
          width: 600,
          borderRadius: 16,
        },
      },
    },
    {
      isFullScreen: true,
      size: 'large',
      css: {
        '@tablet': {
          width: 830,
          borderRadius: 16,
        },
      },
    },
  ],
});

const ModalOverlay = styled(Overlay, {
  background: 'rgba(0, 0, 0, 0.6)',
  zIndex: '$overlay',
  position: 'fixed',
  inset: 0,
  height: '100%',
  display: 'grid',
  placeItems: 'center',
  padding: 16,

  variants: {
    isFullScreen: {
      true: {
        padding: 0,
      },
    },
  },
});

const HeaderContainer = styled('div', {
  background: '#FFFFFF',
  zIndex: '$above1',

  variants: {
    showShadow: {
      true: {
        boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px -8px 28px rgba(0, 0, 0, 0.28)',
        margin: '-16px 0px 0px',
        padding: '16px 16px 10px 16px',
        '@tablet': {
          margin: '-24px 0px 0px',
          padding: '24px 24px 12px 24px',
        },
      },
    },
  },
});

const TitleAndCloseButtonContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',

  variants: {
    isCentered: {
      true: {
        justifyContent: 'flex-end',
      },
    },
  },
});

const TitleContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const Title = styled('div', {
  fontStyle: 'medium',
  fontFamily: '$title !important',
  base: 'L',

  '@tablet': {
    base: 'XL',
  },

  variants: {
    isCentered: {
      true: {
        textAlign: 'center',
      },
    },
    withSubtext: {
      true: {
        marginBottom: 8,
      },
    },
  },
});

const Body = styled('div', {
  height: '100%',
  overflowY: 'auto',
  fontFamily: '$base',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '20px',
  color: '$neutral700',
  paddingY: 24,
  ...StyledScroll,
  '@tablet': {
    fontSize: 16,
  },

  variants: {
    withoutPadding: {
      true: {
        paddingY: '0 !important',
      },
    },
    withSubtext: {
      true: {
        paddingY: 16,
      },
    },
    isCentered: {
      true: {
        paddingTop: 0,
        paddingBottom: 24,
      },
    },
    withoutPaddingBottom: {
      true: {
        paddingTop: 24,
        paddingBottom: 0,
      },
    },
  },
});

const Footer = styled('div', {
  display: 'flex',
  justifyContent: 'end',
  width: '100%',
  gap: 12,
  marginTop: 16,
  '@tablet': {
    marginTop: 24,
  },

  variants: {
    isCentered: {
      true: {
        marginTop: 32,
      },
    },
    showShadow: {
      true: {
        margin: '0px 0px -16px 0',
        padding: '10px 16px 16px 16px',
        '@tablet': {
          margin: '0px 0px -24px 0',
          padding: '12px 24px 24px 24px',
        },
        lift: 'high',
        width: 'auto',
      },
    },
    withoutPadding: {
      true: {
        marginTop: '0 !important',
      },
    },
  },
});
